// Floor sale: read a price tag's barcode with the phone's back camera. Shown
// only where the browser can both open the camera and read barcodes; anywhere
// else the code is typed into the search field instead.
import React, { useEffect, useRef, useState } from "react";

const FORMATS = ["ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "qr_code"];
const EVERY_MS = 250;

export function cameraScanSupported(win = typeof window !== "undefined" ? window : undefined) {
  return Boolean(win?.BarcodeDetector && win.navigator?.mediaDevices?.getUserMedia);
}

export default function FloorCameraScan({ onCode, onClose }) {
  const video = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let stream = null;
    let timer = null;
    let stopped = false;
    const stop = () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
    (async () => {
      try {
        const supported = (await window.BarcodeDetector.getSupportedFormats?.()) || FORMATS;
        const detector = new window.BarcodeDetector({ formats: FORMATS.filter((f) => supported.includes(f)) });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (stopped) return stop();
        const el = video.current;
        el.srcObject = stream;
        await el.play();
        const look = async () => {
          if (stopped) return;
          try {
            const found = await detector.detect(el);
            const code = found.find((b) => String(b.rawValue || "").trim())?.rawValue?.trim();
            if (code && !stopped) {
              stop();
              onCode(code);
              return;
            }
          } catch {
            // A frame that isn't ready yet; try the next one.
          }
          timer = setTimeout(look, EVERY_MS);
        };
        look();
      } catch (e) {
        if (stopped) return;
        setError(
          e?.name === "NotAllowedError"
            ? "Camera access is blocked. Allow the camera for this site, or type the code on the tag."
            : "The camera couldn't start. Type the code on the tag instead."
        );
      }
    })();
    return stop;
  }, [onCode]);

  return (
    <div className="fp-fl-cam" role="dialog" aria-modal="true" aria-label="Scan a price tag">
      {error ? (
        <p className="fp-fl-cam-msg" role="alert">
          {error}
        </p>
      ) : (
        <>
          <video ref={video} className="fp-fl-cam-video" muted playsInline aria-hidden="true" />
          <p className="fp-fl-cam-msg" role="status">
            Point at the price tag barcode.
          </p>
        </>
      )}
      <button type="button" className="fp-fl-btn fp-fl-wide" onClick={onClose}>
        {error ? "Close" : "Cancel"}
      </button>
    </div>
  );
}
